import React, { useState, Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowUpRight, ArrowDownRight, Users, Heart, Target, Wallet as WalletIcon, ShieldCheck, X, Zap, ArrowLeftRight, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatCard } from '../components/ui/StatCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { toast } from 'sonner';
export const Overview: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  const navigate = useNavigate();
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [isKycBannerDismissed, setIsKycBannerDismissed] = useState(false);
  const {
    user,
    transactions,
    campaigns
  } = state;
  const kycTier = user?.kyc?.tier || 0;
  const showKycBanner = kycTier < 3 && !isKycBannerDismissed;
  const kycBannerContent = {
    0: {
      message: 'Your account is unverified. Complete KYC to start transacting.',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      icon: 'text-orange-500',
      btn: 'bg-orange-600 hover:bg-orange-700 text-white'
    },
    1: {
      message: 'Upgrade to Standard (Tier 2) for ₦200,000 daily limit.',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: 'text-blue-500',
      btn: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    2: {
      message: 'Upgrade to Premium (Tier 3) for unlimited transactions.',
      bg: 'bg-brand-primary/10',
      border: 'border-brand-primary/30',
      text: 'text-brand-primary-dark',
      icon: 'text-brand-primary',
      btn: 'bg-brand-primary hover:bg-brand-primary-dark text-white'
    }
  }[kycTier as 0 | 1 | 2];
  // Calculate crowdfunding-focused stats
  const activeCampaigns = campaigns.filter(c => c.status === 'Active').length;
  const totalRaised = campaigns.reduce((sum, c) => sum + c.raisedAmount, 0);
  const totalDonations = transactions.filter(t => t.type === 'Crowdfunding' && t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalDonors = campaigns.reduce((sum, c) => sum + (c.raisedAmount > 0 ? 1 : 0), 0);
  // Calculate Total In and Total Out for Wallet Card
  const totalIn = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
  const totalOut = Math.abs(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0));
  // Filter recent transactions to crowdfunding and wallet only
  const recentTransactions = transactions.filter(t => t.type === 'Crowdfunding' || t.type === 'Wallet').slice(0, 5);
  // Crowdfunding-focused chart data
  const chartData = [{
    name: 'Jan',
    Donations: 15000,
    Raised: 45000
  }, {
    name: 'Feb',
    Donations: 8000,
    Raised: 32000
  }, {
    name: 'Mar',
    Donations: 22000,
    Raised: 67000
  }, {
    name: 'Apr',
    Donations: 12000,
    Raised: 41000
  }, {
    name: 'May',
    Donations: 18000,
    Raised: 55000
  }, {
    name: 'Jun',
    Donations: 25000,
    Raised: 72000
  }];
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type: 'Wallet',
        title: 'Card Funding',
        amount: amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference: `FND-${Date.now().toString().slice(-6)}`
      }
    });
    toast.success(`Successfully added ${formatCurrency(amount)} to your wallet`);
    setIsAddFundsOpen(false);
    setFundAmount('');
  };
  return <div className="space-y-6" data-id="element-1253">
      {/* KYC Banner */}
      <AnimatePresence data-id="element-1254">
        {showKycBanner && kycBannerContent && <motion.div initial={{
        opacity: 0,
        y: -20,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        scale: 0.95,
        height: 0,
        marginBottom: 0
      }} className={`relative overflow-hidden rounded-2xl border ${kycBannerContent.border} ${kycBannerContent.bg} p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm`} data-id="element-1255">
            <div className="flex items-start sm:items-center gap-4" data-id="element-1256">
              <div className={`w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm ${kycBannerContent.icon}`} data-id="element-1257">
                <ShieldCheck size={24} data-id="element-1258" />
              </div>
              <div data-id="element-1259">
                <div className="flex items-center gap-2 mb-1" data-id="element-1260">
                  <h3 className={`font-bold ${kycBannerContent.text}`} data-id="element-1261">
                    KYC Verification
                  </h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-white ${kycBannerContent.text} shadow-sm uppercase tracking-wider`} data-id="element-1262">
                    Tier {kycTier}
                  </span>
                </div>
                <p className={`text-sm font-medium ${kycBannerContent.text} opacity-90`} data-id="element-1263">
                  {kycBannerContent.message}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0" data-id="element-1264">
              <button onClick={() => navigate('/dashboard/settings', {
            state: {
              activeSection: 'kyc'
            }
          })} className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-colors ${kycBannerContent.btn}`} data-id="element-1265">
                Verify Now
              </button>
              <button onClick={() => setIsKycBannerDismissed(true)} className={`p-2 rounded-lg hover:bg-white/50 transition-colors ${kycBannerContent.text} opacity-70 hover:opacity-100`} aria-label="Dismiss" data-id="element-1266">
                <X size={20} data-id="element-1267" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1268">
        <div data-id="element-1269">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-1270">
            Welcome back, {user?.name.split(' ')[0]}! 👋
          </h1>
          <p className="text-sm text-brand-text-light" data-id="element-1271">
            {user?.kyc?.verified ? 'Your account is verified' : 'Complete KYC to unlock full features'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3" data-id="element-1272">
          <button onClick={() => navigate('/dashboard/settings')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10 text-white hover:bg-white/20 transition-colors" data-id="element-1273">
            <ShieldCheck size={18} className="opacity-80" data-id="element-1274" />
            <span className="text-sm font-medium" data-id="element-1275">Settings</span>
          </button>
          <button onClick={() => navigate('/dashboard/wallet')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-primary/10 backdrop-blur-md border border-brand-primary/20 text-white hover:bg-brand-primary/20 transition-colors" data-id="element-1276">
            <WalletIcon size={18} className="opacity-80" data-id="element-1277" />
            <span className="text-sm font-medium" data-id="element-1278">Wallet</span>
          </button>
        </div>
      </div>

      {/* Top Section: Wallet & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1279">
        {/* Modern Wallet Card */}
        <div className="lg:col-span-2 relative bg-gradient-to-br from-[#004d26] via-[#006633] to-[#0A1F0A] rounded-[2rem] p-8 text-white overflow-hidden shadow-xl shadow-brand-primary-dark/20 flex flex-col justify-between min-h-[240px]" data-id="element-1280">
          {/* Abstract Mesh/Glow Effects */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-primary/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-1281" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[60px] translate-y-1/3 -translate-x-1/4 pointer-events-none" data-id="element-1282" />

          {/* Watermark Logo */}
          <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none" data-id="element-1283">
            <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="" className="w-64 h-64 grayscale" data-id="element-1284" />
          </div>

          <div className="relative z-10" data-id="element-1285">
            <div className="flex justify-between items-start mb-2" data-id="element-1286">
              <p className="text-brand-primary-light/80 font-medium tracking-wide uppercase text-sm" data-id="element-1287">
                Total Balance
              </p>
              <div className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-medium border border-white/10 flex items-center gap-1.5" data-id="element-1288">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary-light animate-pulse" data-id="element-1289"></span>
                Active
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6" data-id="element-1290">
              <h2 className="text-5xl sm:text-6xl font-bold tracking-tight text-white drop-shadow-md" data-id="element-1291">
                {formatCurrency(user?.walletBalance || 0)}
              </h2>
              <button onClick={() => setIsAddFundsOpen(true)} className="bg-brand-accent hover:bg-orange-400 text-white px-6 py-3.5 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-brand-accent/20 shrink-0" data-id="element-1292">
                <Plus size={20} data-id="element-1293" /> Add Funds
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm font-medium" data-id="element-1294">
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-1295">
                <div className="w-6 h-6 rounded-full bg-green-400/20 flex items-center justify-center" data-id="element-1296">
                  <ArrowDownRight size={14} className="text-green-300" data-id="element-1297" />
                </div>
                <div data-id="element-1298">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-1299">
                    Total In
                  </div>
                  <div className="text-white" data-id="element-1300">{formatCurrency(totalIn)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-md border border-white/5" data-id="element-1301">
                <div className="w-6 h-6 rounded-full bg-red-400/20 flex items-center justify-center" data-id="element-1302">
                  <ArrowUpRight size={14} className="text-red-300" data-id="element-1303" />
                </div>
                <div data-id="element-1304">
                  <div className="text-white/60 text-[10px] uppercase tracking-wider mb-0.5" data-id="element-1305">
                    Total Out
                  </div>
                  <div className="text-white" data-id="element-1306">{formatCurrency(totalOut)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm flex flex-col justify-center" data-id="element-1307">
          <h3 className="text-sm font-semibold text-brand-text-light uppercase tracking-wider mb-4" data-id="element-1308">
            Quick Actions
          </h3>
          <div className="grid grid-cols-3 gap-3" data-id="element-1309">
            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/crowdfunding')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1310">
              <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-500 flex items-center justify-center group-hover:bg-pink-500 group-hover:text-white transition-colors" data-id="element-1311">
                <Heart size={20} data-id="element-1312" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1313">
                Donate
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/crowdfunding')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1314">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors" data-id="element-1315">
                <Users size={20} data-id="element-1316" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1317">
                Fundraise
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/wallet')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1318">
              <div className="w-10 h-10 rounded-xl bg-brand-primary/10 text-brand-primary-dark flex items-center justify-center group-hover:bg-brand-primary group-hover:text-white transition-colors" data-id="element-1319">
                <WalletIcon size={20} data-id="element-1320" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1321">
                Wallet
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/bills')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1322">
              <div className="w-10 h-10 rounded-xl bg-yellow-50 text-yellow-600 flex items-center justify-center group-hover:bg-yellow-500 group-hover:text-white transition-colors" data-id="element-1323">
                <Zap size={20} data-id="element-1324" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1325">
                Bill Payment
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/diaspora')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1326">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-500 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-colors" data-id="element-1327">
                <ArrowLeftRight size={20} data-id="element-1328" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1329">
                Exchange
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => navigate('/dashboard/facility')} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group" data-id="element-1330">
              <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-500 flex items-center justify-center group-hover:bg-teal-500 group-hover:text-white transition-colors" data-id="element-1331">
                <Building2 size={20} data-id="element-1332" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1333">
                Facility
              </span>
            </motion.button>

            <motion.button whileHover={{
            y: -2,
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => setIsAddFundsOpen(true)} className="flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all group col-span-3" data-id="element-1334">
              <div className="w-10 h-10 rounded-xl bg-orange-50 text-brand-accent flex items-center justify-center group-hover:bg-brand-accent group-hover:text-white transition-colors" data-id="element-1335">
                <Plus size={20} data-id="element-1336" />
              </div>
              <span className="text-xs font-semibold text-brand-text-dark" data-id="element-1337">
                Add Funds
              </span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1338">
        <StatCard title="Active Campaigns" value={activeCampaigns} icon={Users} colorClass="text-blue-600" data-id="element-1339" />
        <StatCard title="Total Raised" value={formatCurrency(totalRaised)} icon={Target} colorClass="text-brand-primary" data-id="element-1340" />
        <StatCard title="My Donations" value={formatCurrency(totalDonations)} icon={Heart} colorClass="text-pink-500" data-id="element-1341" />
        <StatCard title="Campaigns Supported" value={totalDonors} icon={Users} colorClass="text-brand-accent" data-id="element-1342" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1343">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-id="element-1344">
          <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1345">
            Crowdfunding Overview
          </h3>
          <div className="h-72 w-full" data-id="element-1346">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1347">
              <BarChart data={chartData} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} data-id="element-1348">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-1349" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-1350" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={value => `₦${value / 1000}k`} data-id="element-1351" />
                <Tooltip cursor={{
                fill: '#f9fafb'
              }} contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => formatCurrency(value)} data-id="element-1352" />
                <Bar dataKey="Raised" stackId="a" fill="#73C883" radius={[0, 0, 4, 4]} data-id="element-1353" />
                <Bar dataKey="Donations" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} data-id="element-1354" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col" data-id="element-1355">
          <div className="flex items-center justify-between mb-6" data-id="element-1356">
            <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-1357">
              Recent Activity
            </h3>
            <button onClick={() => navigate('/dashboard/wallet')} className="text-sm font-medium text-brand-primary hover:text-brand-primary-dark" data-id="element-1358">
              View All
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4" data-id="element-1359">
            {recentTransactions.length === 0 ? <div className="text-center py-8 text-brand-text-light" data-id="element-1360">
                <Heart size={32} className="mx-auto mb-3 opacity-40" data-id="element-1361" />
                <p className="text-sm" data-id="element-1362">No crowdfunding activity yet.</p>
                <p className="text-xs mt-1" data-id="element-1363">Start by supporting a campaign!</p>
              </div> : <motion.div initial="hidden" animate="visible" variants={{
            visible: {
              transition: {
                staggerChildren: 0.05
              }
            }
          }} className="space-y-3" data-id="element-1364">
                {recentTransactions.map(tx => <motion.div key={tx.id} variants={{
              hidden: {
                opacity: 0,
                y: 10
              },
              visible: {
                opacity: 1,
                y: 0
              }
            }} className="flex items-center justify-between p-3 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-gray-50 transition-all group" data-id="element-1365">
                    <div className="flex items-center gap-3" data-id="element-1366">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-1367">
                        {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-1368" /> : <ArrowUpRight size={20} data-id="element-1369" />}
                      </div>
                      <div className="overflow-hidden" data-id="element-1370">
                        <p className="text-sm font-semibold text-brand-text-dark truncate" data-id="element-1371">
                          {tx.title}
                        </p>
                        <p className="text-xs text-brand-text-light" data-id="element-1372">
                          {formatDate(tx.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0" data-id="element-1373">
                      <p className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-1374">
                        {tx.amount > 0 ? '+' : ''}
                        {formatCurrency(tx.amount)}
                      </p>
                      <div className="mt-1 flex justify-end" data-id="element-1375">
                        <StatusBadge status={tx.status} data-id="element-1376" />
                      </div>
                    </div>
                  </motion.div>)}
              </motion.div>}
          </div>
        </div>
      </div>

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-1377">
        <form onSubmit={handleAddFunds} className="space-y-4" data-id="element-1378">
          <div data-id="element-1379">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1380">
              Amount (₦)
            </label>
            <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="Enter amount" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" min="1000" required data-id="element-1381" />
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-1382">
            <p className="text-sm font-medium text-gray-700" data-id="element-1383">Payment Method</p>
            <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg" data-id="element-1384">
              <div className="w-8 h-5 bg-blue-600 rounded flex items-center justify-center text-[8px] text-white font-bold" data-id="element-1385">
                VISA
              </div>
              <div className="flex-1" data-id="element-1386">
                <p className="text-sm font-medium" data-id="element-1387">•••• •••• •••• 4242</p>
              </div>
            </div>
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3 rounded-xl font-semibold transition-colors mt-4" data-id="element-1388">
            Fund Wallet
          </button>
        </form>
      </Modal>
    </div>;
};